/**
 * Delete-lessor confirmation dialog.
 *
 * Two-mode behavior:
 *   - No properties linked → simple confirm (single click + Cancel).
 *   - N properties linked → user must type the lessor's name to enable
 *     the destructive button. Saves a class of accidental cascade-clears.
 */
import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/contexts/LanguageContext";
import type { Lessor } from "@/types/lessor";

type Props = {
  lessor: Lessor | null;
  linkedPropertyCount: number;
  /**
   * Apartments whose EFFECTIVE lessor is this owner. Counted separately from
   * properties because most owners hold apartments and no property at all —
   * gating only on properties let those owners delete with no warning while
   * `apartments.lessor_id` (ON DELETE SET NULL) quietly went blank.
   */
  linkedApartmentCount: number;
  openInvoiceCount: number;
  isDeleting: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
};

export function LessorDeleteDialog({
  lessor,
  linkedPropertyCount,
  linkedApartmentCount,
  openInvoiceCount,
  isDeleting,
  onConfirm,
  onCancel,
}: Props) {
  const { t } = useLanguage();
  const [typedName, setTypedName] = useState("");
  const [reason, setReason] = useState("");
  const requiresConfirmation =
    linkedPropertyCount > 0 || linkedApartmentCount > 0;
  const namesMatch =
    typedName.trim() === (lessor?.name ?? "").trim() && typedName.length > 0;
  // A reason is always required; when properties are linked the name must
  // also match before the destructive action unlocks. Non-canceled invoices
  // block deletion outright (mirrors the admin_delete_lessor RPC guard) —
  // lessor_id on management_invoices is ON DELETE SET NULL, not cascaded, so
  // deleting the owner would otherwise orphan real financial records.
  const hasOpenInvoices = openInvoiceCount > 0;
  const hasReason = reason.trim().length > 0;
  const canConfirm =
    !hasOpenInvoices && hasReason && (requiresConfirmation ? namesMatch : true);

  // Reset fields whenever the dialog opens for a different lessor
  useEffect(() => {
    setTypedName("");
    setReason("");
  }, [lessor?.id]);

  return (
    <AlertDialog open={!!lessor} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            {t("admin.lessor.deleteTitle", "Delete this owner?")}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                <strong className="text-foreground">{lessor?.name}</strong>{" "}
                {t(
                  "admin.lessor.deleteWillBePermanent",
                  "will be permanently deleted. This action cannot be undone."
                )}
              </p>

              {hasOpenInvoices ? (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 space-y-1">
                  <p className="font-medium text-foreground">
                    {openInvoiceCount === 1
                      ? t(
                          "admin.lessor.oneOpenInvoice",
                          "1 non-canceled invoice references this owner."
                        )
                      : `${openInvoiceCount} ${t("admin.lessor.nOpenInvoicesSuffix", "non-canceled invoices reference this owner.")}`}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "admin.lessor.settleInvoicesFirst",
                      "Deleting would orphan those financial records. Cancel or settle them first, then come back to delete this owner."
                    )}
                  </p>
                </div>
              ) : null}

              {requiresConfirmation ? (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 space-y-1">
                  <p className="font-medium text-foreground">
                    {[
                      linkedApartmentCount > 0
                        ? `${linkedApartmentCount} apartment${
                            linkedApartmentCount === 1 ? " is" : "s are"
                          }`
                        : null,
                      linkedPropertyCount > 0
                        ? `${linkedPropertyCount} propert${
                            linkedPropertyCount === 1 ? "y is" : "ies are"
                          }`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(` ${t("admin.lessor.and", "and")} `)}{" "}
                    {t("admin.lessor.linkedToOwner", "linked to this owner.")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "admin.lessor.willBecomeEmptyHint",
                      "They will keep existing, but their lessor field will become empty. You'll need to assign a different lessor or create a new one."
                    )}
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground">
                  {t(
                    "admin.lessor.noPropertiesLinked",
                    "No apartments or properties are linked."
                  )}
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="delete-reason" className="text-xs font-medium">
              {t("admin.lessor.deleteReasonLabel", "Reason for deletion")}{" "}
              <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="delete-reason"
              rows={2}
              placeholder={t(
                "admin.lessor.deleteReasonPlaceholder",
                "e.g. Duplicate record, owner offboarded, created by mistake…"
              )}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isDeleting}
            />
            {!hasReason && (
              <p className="text-xs text-muted-foreground">
                {t(
                  "admin.lessor.deleteReasonRequired",
                  "A reason is required before you can delete this owner."
                )}
              </p>
            )}
          </div>

          {requiresConfirmation && (
            <div className="space-y-1.5">
              <Label
                htmlFor="confirm-name"
                className="text-xs font-normal text-muted-foreground"
              >
                {t("admin.delete.typePrefix", "Type")}{" "}
                <span className="font-mono text-foreground">
                  {lessor?.name}
                </span>{" "}
                {t("admin.delete.typeSuffix", "to confirm")}
              </Label>
              <Input
                id="confirm-name"
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                disabled={isDeleting}
              />
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            {t("admin.action.cancel", "Cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onConfirm(reason.trim())}
            disabled={!canConfirm || isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting
              ? t("admin.action.deleting", "Deleting…")
              : t("admin.lessor.deleteOwner", "Delete owner")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
